import React, { useState } from 'react';
import { ExternalLink, Star, Download, Users, AlertCircle, Building, Smartphone } from 'lucide-react';
import { projects as mockProjects } from '@/mock';
import '../styles/ProjectsSection.css';

const ProjectsSection = ({ projects: propsProjects, loading }) => {
  const [filter, setFilter] = useState('all');
  
  // Use props projects if available, otherwise use mock
  const projects = propsProjects && propsProjects.length > 0 ? propsProjects : mockProjects;

  const categories = ['all', ...new Set(projects.map(p => p.category))];
  
  const filteredProjects = filter === 'all' 
    ? projects 
    : projects.filter(p => p.category === filter);

  const getIcon = (iconName) => {
    const icons = {
      Star: Star,
      Download: Download,
      Users: Users,
      AlertCircle: AlertCircle,
      Building: Building,
      Smartphone: Smartphone
    };
    const IconComponent = icons[iconName] || Star;
    return <IconComponent size={16} />;
  };

  const handleProjectClick = (project) => {
    if (project.link && project.link !== '#') {
      window.open(project.link, '_blank', 'noopener,noreferrer');
    }
  };

  if (loading) {
    return (
      <section className="projects-section" id="projects">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Featured Projects</h2>
            <div className="title-underline"></div>
          </div>
          <div style={{ textAlign: 'center', color: '#a3a3a3', padding: '3rem' }}>
            Loading projects...
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="projects-section" id="projects">
      <div className="section-container">
        <div className="section-header">
          <h2 className="section-title">Featured Projects</h2>
          <div className="title-underline"></div>
          <p className="section-subtitle">Building impactful mobile solutions</p>
        </div>

        <div className="project-filters">
          {categories.map((category) => (
            <button
              key={category}
              className={`filter-btn ${filter === category ? 'active' : ''}`}
              onClick={() => setFilter(category)}
            >
              {category.charAt(0).toUpperCase() + category.slice(1)}
            </button>
          ))}
        </div>

        <div className="projects-grid">
          {filteredProjects.map((project, index) => (
            <div 
              key={project.id} 
              className={`project-card ${project.link && project.link !== '#' ? 'clickable' : ''}`}
              style={{ animationDelay: `${index * 0.1}s` }}
              onClick={() => handleProjectClick(project)}
              data-testid={`project-card-${project.id}`}
            >
              <div className="project-image-container">
                <img src={project.image} alt={project.title} className="project-image" />
                <div className="project-overlay">
                  {project.link && project.link !== '#' && (
                    <span className="overlay-btn">
                      <ExternalLink size={20} />
                    </span>
                  )}
                </div>
              </div>

              <div className="project-content">
                <div className="project-header">
                  <span className="project-category">{project.category}</span>
                  <span className="project-duration">{project.duration}</span>
                </div>

                <h3 className="project-title">{project.title}</h3>
                <p className="project-description">{project.description}</p>

                <div className="project-metrics">
                  {project.metrics.map((metric, idx) => (
                    <div key={idx} className="metric-item">
                      <div className="metric-icon">
                        {getIcon(metric.icon)}
                      </div>
                      <div className="metric-info">
                        <div className="metric-value">{metric.value}</div>
                        <div className="metric-label">{metric.label}</div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="project-tech">
                  {project.technologies.map((tech, idx) => (
                    <span key={idx} className="tech-badge">{tech}</span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProjectsSection;